/**
@author Geethananth M
Use this file to write functions that can be used across the app
* */

import dayjs from 'dayjs';
import uniqid from 'uniqid';
import ApiCaller from '../services/api/general';

/**
 * Returns a unique random string
 * @param {string} prefix 
 * @param {string} suffix 
 * @returns string the generated string
 */
export const getUniqueId = (prefix = '', suffix = '') => uniqid(prefix, suffix);


/**
 * Round of the number to nearest 
 * @param {Number} num 
 * @returns Number
 */
export const roundOff = (num) => Math.round((num + Number.EPSILON) * 100) / 100;


/**
 * Returns a currency formatted string
 * @param {string | number} input 
 * @param {boolean} includeUSDSign
 * @returns string the generated string
 */
export const usdFormatter = (input, includeUSDSign = false) => {
    const numberAs = Math.floor(input * 100) / 100;
    // Create our number formatter.
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2,

        // These options are needed to round to whole numbers if that's what you want.
        // minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        // maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
    });
    const replacer = includeUSDSign ? '$' : '';
    return formatter.format(numberAs).replace("$", replacer);
}

/**
 * Formats the birthday to age
 * @param {string} birthday 
 */
export const getAgeFromBirthday = (birthday) => {
    let result = '';
    if (birthday) {
        const months = dayjs().diff(dayjs(birthday), 'month');
        if (months < 12) {
            result = `${months} months`;
        } else {
            const years = parseInt((months / 12), 10);
            if (years === 1) {
                result = `1 year`;
            } else {
                result = `${years} years`;
            }
        }
    }
    return result;
}


/**
* Download a file using its url
* @param {string} fileURL;
* @param {string} fileName;
* @returns void
*/
export const downloadFileByURL = async (fileURL, fileName = 'file') => {
    try {
        const a = document.createElement('a')
        a.href = fileURL
        a.target = '_blank';
        a.download = fileName;
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
    } catch (e) {
        console.log(e)
        // return {success:false, message: 'Could not download the PDF at this time. Please try again later'};
    }
}

/**
 * Return the current facility URL
 * @returns String the current facility slug
 */
export const getCurrentFacilityURL = () => {
    const facilitySlug = window.location.pathname.split('/')[1];
    return facilitySlug || '';

}


export const getURLExtension = (url) => url.split(/[#?]/)[0].split('.').pop().trim();

/**
 * Just to check the auth token validation by calling random API
 */

export const authCheck = () => {
    const apiCaller = new ApiCaller('auth');
    apiCaller.getData(('auth-check'))
}

/**
 * Converts the seconds to HH min format
 * @param {*} sec 
 * @returns string
 */
export const convertSecondsToHoursMins = (sec) => {
    // calculate hours, minutes and seconds
    const hrs = Math.floor(sec / 3600);
    const min = Math.floor((sec - (hrs * 3600)) / 60);
    const seconds = sec - (hrs * 3600) - (min * 60);

    // format hours, minutes and seconds
    const hrsFormatted = hrs < 10 ? `0${hrs}` : hrs;
    const minFormatted = min < 10 ? `0${min}` : min;
    const secondsFormatted = seconds < 10 ? `0${seconds}` : seconds;

    // return formatted string
    if (hrsFormatted === '00' && minFormatted === '00') {
        return `${secondsFormatted} seconds`
    }
    return `${hrsFormatted}h ${minFormatted} min`;
}


/**
 * Converts the file size to user readable form
 * @param {string/number} bytes 
 * @returns string
 */
export const formatFileSize = (bytes) => {
    const KB = 1024; // 1 KB = 1024 bytes
    const MB = 1024 * 1024; // 1 MB = 1024 * 1024 bytes
    const GB = 1024 * 1024 * 1024; // 1 GB = 1024 * 1024 * 1024 bytes

    if (bytes >= GB) {
        return `${(bytes / GB).toFixed(2)} GB`; // Convert to GB using template literals
    }
    if (bytes >= MB) {
        return `${(bytes / MB).toFixed(2)} MB`; // Convert to MB using template literals
    }
    if (bytes >= KB) {
        return `${(bytes / KB).toFixed(2)} KB`; // Convert to GB using template literals
    }
    return `${bytes} bytes`; // Return bytes if less than 1 MB using template literals
}


/**
 * Function to match a MIME type to a general file category
 * @param {string} mimeType - The MIME type of the file (e.g. "application/pdf")
 * @returns {string|null} - The general category name (e.g. "document") or null if not matched
 */

export const getCategoryFromMimeType = (mimeType) => {
    if (!mimeType || typeof mimeType !== 'string') return null;

    const mimeTypeLower = mimeType.toLowerCase();

    const mimeCategoryMap = {
        image: [
            'image/jpeg',
            'image/png',
            'image/gif',
            'image/bmp',
            'image/svg+xml',
            'image/webp',
            'image/tiff'
        ],
        video: [
            'video/mp4',
            'video/x-msvideo',      // avi
            'video/quicktime',      // mov
            'video/x-ms-wmv',       // wmv
            'video/x-flv',          // flv
            'video/x-matroska',     // mkv
            'video/webm'
        ],
        audio: [
            'audio/mpeg',           // mp3
            'audio/wav',
            'audio/aac',
            'audio/flac',
            'audio/ogg',
            'audio/mp4',
            'audio/webm'
        ],
        document: [
            'application/pdf',
            'application/msword',               // .doc
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
            'application/vnd.ms-excel',         // .xls
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',     // .xlsx
            'application/vnd.ms-powerpoint',    // .ppt
            'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
            'text/plain',
            'application/rtf'
        ],
        archive: [
            'application/zip',
            'application/x-rar-compressed',
            'application/x-7z-compressed',
            'application/x-tar',
            'application/gzip'
        ]
    };

    // Use Object.entries and find to get the category
    const category = Object.entries(mimeCategoryMap).find(([_, mimeList]) =>
        mimeList.includes(mimeTypeLower)
    );

    return category ? category[0] : null; // Return the category name or null
};
